/** UI Context Refreshed **/
import React, { useReducer, useCallback, useMemo, useEffect, useState } from 'react';

import { UIContext } from './UIContext';

const initialState = {
    pageTitle: '',
    isScrolled: false,
    showUnlockModal: false,
    isBooting: false, // Core Global Transition State
    terminalBranding: { title: '', subtitle: '' },
    terminalStep: 1,
    terminalMode: 'WITHDRAWAL',
    isFullscreen: !!document.fullscreenElement
};

function uiReducer(state, action) {
    switch (action.type) {
        case 'SET_PAGE_TITLE': return { ...state, pageTitle: action.payload };
        case 'SET_SCROLLED': return { ...state, isScrolled: action.payload };
        case 'SET_UNLOCK_MODAL': return { ...state, showUnlockModal: action.payload };
        case 'SET_BOOTING': return { ...state, isBooting: action.payload };
        case 'SET_TERMINAL_BRANDING': return { ...state, terminalBranding: action.payload };
        case 'SET_TERMINAL_STEP': return { ...state, terminalStep: action.payload };
        case 'SET_TERMINAL_MODE': return { ...state, terminalMode: action.payload };
        case 'SET_FULLSCREEN': return { ...state, isFullscreen: action.payload };
        default: return state;
    }
}

export const UIProvider = ({ children }) => {
    const [state, dispatch] = useReducer(uiReducer, initialState);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        
        const handleFSChange = () => {
            dispatch({ type: 'SET_FULLSCREEN', payload: !!document.fullscreenElement });
        };

        document.addEventListener('fullscreenchange', handleFSChange);
        
        return () => {
            clearInterval(timer);
            document.removeEventListener('fullscreenchange', handleFSChange);
        };
    }, []);

    const setPageTitle = useCallback((title) => dispatch({ type: 'SET_PAGE_TITLE', payload: title }), []);
    const setIsScrolled = useCallback((scrolled) => dispatch({ type: 'SET_SCROLLED', payload: scrolled }), []);
    const setShowUnlockModal = useCallback((show) => dispatch({ type: 'SET_UNLOCK_MODAL', payload: show }), []);
    const setBooting = useCallback((booting) => dispatch({ type: 'SET_BOOTING', payload: booting }), []);
    const setTerminalBranding = useCallback((branding) => dispatch({ type: 'SET_TERMINAL_BRANDING', payload: branding }), []);
    const setTerminalStep = useCallback((step) => dispatch({ type: 'SET_TERMINAL_STEP', payload: step }), []);
    const setTerminalMode = useCallback((mode) => dispatch({ type: 'SET_TERMINAL_MODE', payload: mode }), []);

    const toggleFullscreen = useCallback(async () => {
        try {
            if (!document.fullscreenElement) {
                if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                }
            }
        } catch (err) {
            console.warn("Fullscreen toggle failed:", err);
        }
    }, []);

    const value = useMemo(() => ({
        ...state,
        currentTime,
        setPageTitle,
        setIsScrolled,
        setShowUnlockModal,
        setBooting,
        setTerminalBranding,
        setTerminalStep,
        setTerminalMode,
        toggleFullscreen,
        dispatch
    }), [state, currentTime, setPageTitle, setIsScrolled, setShowUnlockModal, setBooting, setTerminalBranding, setTerminalStep, setTerminalMode, toggleFullscreen]);

    return (
        <UIContext.Provider value={value}>
            {children}
        </UIContext.Provider>
    );
};
